import axios from 'axios';
import dayjs from 'dayjs';
import i18n from '@/i18n';
import { stripZone } from '@/services/dateValue';

const DEFAULT_METRIC = 'DEAL_PREMIUM';

// The three lead-insurance goal groups shown as separate trackers (order = display order).
// Each group is stored on the backend as its own metric; COMMON reuses the legacy DEAL_PREMIUM
// goals, so previously saved goals become the common group's goals.
// premiumLabelKey is the group's own name for the tracked amount (AHORRO/INVERSION rename
// "Deal premium" to "Plan value" / "Money deposited" everywhere it is shown).
export const GOAL_GROUPS = [
  {
    group: 'COMMON', metric: 'DEAL_PREMIUM', labelKey: 'salesGoals.GroupCommon', premiumLabelKey: 'leads.form.dealPremium',
  },
  {
    group: 'AHORRO', metric: 'DEAL_PREMIUM_AHORRO', labelKey: 'salesGoals.GroupAhorro', premiumLabelKey: 'leads.form.dealPremiumAhorro',
  },
  {
    group: 'INVERSION', metric: 'DEAL_PREMIUM_INVERSION', labelKey: 'salesGoals.GroupInversion', premiumLabelKey: 'leads.form.dealPremiumInversion',
  },
];

// Goals span a month range. The backend normalizes any instant to the month start, so we send the
// first day of the start/end month at midnight using the same date format the dashboard already sends
// to /insurance/metric/lead/detail. A single-month goal has periodStart === periodEnd.
// Strip the Z first: the backend sends UTC month starts, and parsing them as instants would pull
// the 1st back into the previous month west of UTC (Mexico saw August while on September).
export const toPeriodMonth = (date) => dayjs(stripZone(date) || undefined)
  .startOf('month')
  .format('YYYY-MM-DDTHH:mm:ss');

// Alias for the end month of a range (kept explicit for readability at call sites).
export const toPeriodEnd = (date) => toPeriodMonth(date);

// Maps a backend GoalProgress to the dual-axis object the dashboard renders (amount + policy
// count). Any count field missing from an older backend payload becomes null/0, which collapses
// the UI to the amount-only (v1) rendering. Returns null for a missing progress so callers can
// chain safely.
export const normalizeGoalProgress = (p) => (p ? {
  goalAmount: p.target ?? null,
  achievedAmount: p.achieved ?? 0,
  pctAmount: p.percent ?? null,
  paceAmount: p.paceExpected ?? null,
  onTrackAmount: p.onTrack ?? null,
  completedAmount: p.completed ?? false,
  goalCount: p.targetCount ?? null,
  achievedCount: p.achievedCount ?? 0,
  pctCount: p.percentCount ?? null,
  paceCount: p.paceExpectedCount ?? null,
  onTrackCount: p.onTrackCount ?? null,
  completedCount: p.completedCount ?? false,
} : null);

const state = {
  salesGoalTracker: null,
};

const getters = {
  salesGoalTracker: (state) => state.salesGoalTracker,
  // Per-group trackers keyed by group name (COMMON / AHORRO / INVERSION). Empty object while the
  // backend predates the `groups` field.
  salesGoalTrackerGroups: (state) => (state.salesGoalTracker?.groups || []).reduce((acc, group) => {
    acc[group.group] = group;
    return acc;
  }, {}),
};

const mutations = {
  SET_SALES_GOAL_TRACKER(state, tracker) {
    state.salesGoalTracker = tracker;
  },
  CLEAR_SALES_GOAL_TRACKER(state) {
    state.salesGoalTracker = null;
  },
};

const headers = () => ({ headers: { 'X-Locale': i18n.locale } });

const actions = {
  // Prefill a modal with the currently-saved goals for a period (periodStart/periodEnd). When
  // agentUserId is set (per-agent modal), the backend resolves that agent's goal across its OWN
  // (possibly custom) period and returns each agent goal with its stored periodStart/periodEnd.
  async getSalesGoal(_, {
    periodStart, periodEnd, metric = DEFAULT_METRIC, agentUserId = null,
  } = {}) {
    const response = await axios.post(
      '/insurance/sales-goal/get',
      {
        periodStart, periodEnd, metric, agentUserId
      },
      headers(),
    );
    return response.data;
  },
  // Create/edit the company + per-agent goals for a period (upsert + remove-omitted). Returns saved
  // state. Targets are dual-axis: companyTarget/companyTargetCount and per-agent
  // {agentUserId, target, targetCount} — either axis may be null.
  async saveSalesGoal(_, payload) {
    const response = await axios.post(
      '/insurance/sales-goal/save',
      { metric: DEFAULT_METRIC, ...payload },
      headers(),
    );
    return response.data;
  },
  // Set/edit a SINGLE agent's goal for a period (leaderboard "Establecer meta"). Leaves company +
  // other agents untouched.
  async saveAgentSalesGoal(_, payload) {
    const response = await axios.post(
      '/insurance/sales-goal/agent/save',
      { metric: DEFAULT_METRIC, ...payload },
      headers(),
    );
    return response.data;
  },
  // Clear the company goal + all agent goals for the period.
  async deleteSalesGoal(_, { periodStart, periodEnd, metric = DEFAULT_METRIC } = {}) {
    const response = await axios.post(
      '/insurance/sales-goal/delete',
      { periodStart, periodEnd, metric },
      headers(),
    );
    return response.data;
  },
  // Read-only dashboard progress for the company, each agent and the caller. The goal period shown is
  // the one intersecting the dashboard filter window [periodFrom, periodTo].
  async fetchSalesGoalTracker({ commit }, { periodFrom, periodTo, metric = DEFAULT_METRIC } = {}) {
    try {
      const response = await axios.post(
        '/insurance/sales-goal/tracker',
        { periodFrom, periodTo, metric },
        headers(),
      );
      commit('SET_SALES_GOAL_TRACKER', response.data);
      return response.data;
    } catch (error) {
      commit('SET_SALES_GOAL_TRACKER', null);
      console.error('Error fetching sales goal tracker:', error);
      return null;
    }
  },
  clearSalesGoalTracker({ commit }) {
    commit('CLEAR_SALES_GOAL_TRACKER');
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
